import React, { useState, useEffect, useMemo, useRef } from 'react';
import SeatPill from '../components/common/SeatPill';
import {
  ConfigProvider,
  theme as antdTheme,
  Typography,
  Tag,
  Button,
  Space,
  Select,
  Badge,
  Empty,
  Spin,
  Tooltip,
  Drawer,
  Tabs,
  Input,
  List,
  Switch,
  App,
  Collapse,
  Segmented,
} from 'antd';
import {
  SoundOutlined,
  SettingOutlined,
  DeleteOutlined,
  PlusOutlined,
  WarningOutlined,
  SearchOutlined,
  ClearOutlined,
  ExclamationCircleOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  TagsOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';
import {
  useGetKitchenOrdersQuery,
  useGetKitchenAreasQuery,
  useMarkKitchenOrderReadyMutation,
  useMarkKitchenOrderCompletedMutation,
  useCompleteKitchenOrderItemsMutation,
  useUpdateKitchenOrderItemsStatusMutation,
  useUpdateKitchenOrderStatusMutation,
  useGetKitchenStatsQuery,
  useGetKitchenNotesQuery,
  useCreateKitchenNoteMutation,
  useDeleteKitchenNoteMutation,
} from '../services/kitchenApi';
import {
  useGetProductsQuery,
  useUpdateProductAvailabilityMutation,
} from '../services/productsApi';
import { Product } from '../types/products';
import {
  useGetProductionCentersForKitchenDisplayQuery,
  useHasProductionCentersQuery,
} from '../services/productionCenterApi';
import {
  useGetBoolSettingQuery,
  useUpdateSettingMutation,
} from '../services/settingsApi';
import { useGetActiveCashRegistersQuery } from '../services/cashRegisterApi';
import { KitchenOrderDto, KitchenOrderStatus, KdsOrderItemDto } from '../types/kitchen';
import { useResponsive } from '../hooks/useResponsive';
import { useHubEvent } from '../hooks/useHubEvent';
import { KITCHEN_ORDERS_CHANGED_EVENT, RECONNECTED_EVENT } from '../services/realtimeHub';
import { useIsModuleEnabled } from '../hooks/useIsModuleEnabled';
import { MODULE_KEYS } from '../constants/moduleKeys';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import KitchenStockDrawerTab from '../components/menu/KitchenStockDrawerTab';
import KitchenOrderCard from '../components/kitchen/KitchenOrderCard';
import GroupedDishCard from '../components/kitchen/GroupedDishCard';
import KitchenRailsView from '../components/kitchen/KitchenRailsView';
import {
  KdsOrderWithTime,
  effectiveStatusOf,
  groupAllOrdersByDish,
  prepDurationMinutes,
} from '../components/kitchen/kdsHelpers';
import '../components/kitchen/kitchen.css';

const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

type KdsViewMode = 'lanes' | 'dish' | 'rails';
const KDS_VIEWS: KdsViewMode[] = ['lanes', 'dish', 'rails'];
// Por pantalla: cada estación de cocina escoge la vista que le acomoda.
const KDS_VIEW_KEY = 'kitchen.viewMode';

const readKdsView = (): KdsViewMode => {
  try {
    const saved = localStorage.getItem(KDS_VIEW_KEY) as KdsViewMode | null;
    return saved && KDS_VIEWS.includes(saved) ? saved : 'lanes';
  } catch {
    return 'lanes';
  }
};

const renderHistoryBody = (order: KitchenOrderDto): React.ReactNode => {
  const items = order.items ?? [];
  if (order.isDailyMenuOrder) {
    const byCourse = new Map<number, KdsOrderItemDto[]>();
    for (const it of items) {
      const arr = byCourse.get(it.sortOrder) ?? [];
      arr.push(it);
      byCourse.set(it.sortOrder, arr);
    }
    const courses = Array.from(byCourse.entries()).sort(([a], [b]) => a - b);
    return (
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {courses.map(([sortOrder, courseItems]) => (
          <div key={sortOrder}>
            <Text strong style={{ fontSize: 12 }}>{`Tiempo ${sortOrder + 1}`}</Text>
            <div style={{ marginLeft: 12 }}>
              {courseItems.map(it => (
                <div
                  key={it.id}
                  style={{
                    opacity: it.isCancelled ? 0.5 : 1,
                    textDecoration: it.isCancelled ? 'line-through' : 'none',
                  }}
                >
                  <Text>{it.seatNumber && <SeatPill seatNumber={it.seatNumber} size="small" style={{ marginRight: 6 }} />}{it.quantity}x {it.productName}</Text>
                  {it.variants && <Text type="secondary"> ({it.variants})</Text>}
                  {it.notes && (
                    <Text type="secondary" italic style={{ fontSize: 11, display: 'block', marginLeft: 16 }}>
                      Nota: {it.notes}
                    </Text>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        {order.completedByUserName && (
          <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
            Despachado por {order.completedByUserName}
          </Text>
        )}
      </Space>
    );
  }
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {items.map(it => (
        <div
          key={it.id}
          style={{
            opacity: it.isCancelled ? 0.5 : 1,
            textDecoration: it.isCancelled ? 'line-through' : 'none',
          }}
        >
          <Text>{it.seatNumber && <SeatPill seatNumber={it.seatNumber} size="small" style={{ marginRight: 6 }} />}{it.quantity}x {it.productName}</Text>
          {it.variants && <Text type="secondary"> ({it.variants})</Text>}
          {it.notes && (
            <Text type="secondary" italic style={{ fontSize: 11, display: 'block', marginLeft: 16 }}>
              Nota: {it.notes}
            </Text>
          )}
        </div>
      ))}
      {order.completedByUserName && (
        <Text type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
          Despachado por {order.completedByUserName}
        </Text>
      )}
    </Space>
  );
};
const KitchenDisplay: React.FC = () => {
  // Contextual message/modal from App.useApp — the static `message` import silently
  // no-ops under AntD v5 (no context), so everything here must use these instances.
  const { modal, message } = App.useApp();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Persisted filters in localStorage
  const [areaFilter, setAreaFilter] = useState<string | undefined>(() => {
    const saved = localStorage.getItem('kitchenAreaFilter');
    return saved || undefined;
  });
  const [productionCenterFilters, setProductionCenterFilters] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('kitchenProductionCenterFilters');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [includeCompleted] = useState(false);
  const [kdsView, setKdsViewState] = useState<KdsViewMode>(readKdsView);
  const setKdsView = (view: KdsViewMode) => {
    setKdsViewState(view);
    try {
      localStorage.setItem(KDS_VIEW_KEY, view);
    } catch {
      // Sin almacenamiento la vista dura lo que la sesión.
    }
  };
  const { isMobile } = useResponsive();

  // Maximized mode: a fixed overlay (CSS class) hides the app sidebar/header, and we
  // additionally request browser fullscreen on <html>. Fullscreen goes on the document
  // element — NOT this page's div — because AntD portals (Drawer, Select dropdowns,
  // modal.confirm, message toasts) mount on document.body: with a child element
  // fullscreened the browser only paints that subtree and every portal disappears.
  // On browsers without the API (iOS Safari) the overlay alone still works.
  const pageRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    // Sync when the user exits browser fullscreen via Esc.
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) setIsFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);
  const toggleFullscreen = () => {
    if (isFullscreen) {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    } else {
      document.documentElement.requestFullscreen?.()?.catch(() => {});
      setIsFullscreen(true);
    }
  };

  // Drawer state for product/notes management
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [newNoteMessage, setNewNoteMessage] = useState('');
  const [productSearch, setProductSearch] = useState('');

  // Persist area filter to localStorage
  useEffect(() => {
    if (areaFilter) {
      localStorage.setItem('kitchenAreaFilter', areaFilter);
    } else {
      localStorage.removeItem('kitchenAreaFilter');
    }
  }, [areaFilter]);

  // Persist production center filters to localStorage
  useEffect(() => {
    if (productionCenterFilters.length > 0) {
      localStorage.setItem('kitchenProductionCenterFilters', JSON.stringify(productionCenterFilters));
    } else {
      localStorage.removeItem('kitchenProductionCenterFilters');
    }
  }, [productionCenterFilters]);

  // Sound notification state (persisted in localStorage)
  const [soundEnabled, setSoundEnabled] = useState(() =>
    localStorage.getItem('kitchenSoundEnabled') !== 'false'
  );
  const audioContextRef = useRef<AudioContext | null>(null);

  // Play notification sound using Web Audio API
  const playNotificationSound = () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioContextRef.current;

      // Play two beeps
      const playBeep = (startTime: number, frequency: number, duration: number) => {
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';

        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      const now = ctx.currentTime;
      playBeep(now, 800, 0.15);         // First beep
      playBeep(now + 0.2, 1000, 0.15);  // Second beep (higher pitch)
      playBeep(now + 0.4, 800, 0.15);   // Third beep
    } catch (err) {
      // silently ignore
    }
  };

  // Fetch production centers for KDS filter
  const { data: productionCenters = [] } = useGetProductionCentersForKitchenDisplayQuery();
  const { data: hasProductionCentersData } = useHasProductionCentersQuery();
  const hasProductionCenters = hasProductionCentersData?.hasProductionCenters ?? false;

  // KDS Enabled setting (only relevant when no production centers exist)
  const { data: kdsEnabledSetting, isLoading: isLoadingKdsSetting } = useGetBoolSettingQuery('KitchenDisplayEnabled');
  const [updateSetting, { isLoading: isUpdatingSetting }] = useUpdateSettingMutation();

  // Inventory access — controls Stock tab visibility in management drawer. Pro plan
  // auto-enables the module, so use useIsModuleEnabled instead of reading the flag raw.
  const { enabled: isInventarioEnabled } = useIsModuleEnabled(MODULE_KEYS.Inventario);

  // Determine if KDS should show orders
  // If production centers exist -> always show (ignore setting)
  // If no production centers -> only show if KitchenDisplayEnabled = true
  const kdsEnabled = hasProductionCenters || (kdsEnabledSetting?.value ?? false);

  // When no manual filter is selected but production centers exist,
  // auto-filter to ShowOnScreen=true centers so print-only items don't appear on KDS
  const hasManualFilter = productionCenterFilters.length > 0;
  const effectiveProductionCenterIds = hasManualFilter
    ? productionCenterFilters
    : productionCenters.length > 0
      ? productionCenters.map(pc => pc.id)
      : undefined;

  // Fetch kitchen orders. Real-time: el evento SignalR KitchenOrdersChanged dispara el
  // refetch al instante; el polling queda como fallback lento por si el websocket se cae.
  const { data: ordersResponse, isLoading, refetch } = useGetKitchenOrdersQuery(
    {
      areaFilter,
      productionCenterIds: effectiveProductionCenterIds,
      includeCompleted,
      // Only include uncategorized items (products with no production center)
      // when no manual filter is applied — otherwise the user has explicitly chosen
      // specific centers and we should respect that.
      includeUncategorized: !hasManualFilter,
    },
    { pollingInterval: 30000, skip: !kdsEnabled }
  );

  // Dispatch history tab: only query once the user opens the tab inside the "Gestión" drawer
  const [historyTabOpened, setHistoryTabOpened] = useState(false);
  const { data: activeCashRegister } = useGetActiveCashRegistersQuery();
  const completedSince = useMemo(() => {
    const raw = activeCashRegister?.openTime;
    if (raw) {
      return /Z$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : new Date(raw + 'Z').toISOString();
    }
    // Fallback: today's UTC midnight. Avoids local->UTC drift mid-request.
    return new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').toISOString();
  }, [activeCashRegister?.openTime]);

  const { data: historyData, isFetching: historyLoading } = useGetKitchenOrdersQuery(
    {
      includeCompleted: true,
      completedSince,
      orderByCompletedAtDesc: true,
      productionCenterIds: effectiveProductionCenterIds,
      includeUncategorized: !hasManualFilter,
    },
    { skip: !drawerVisible || !historyTabOpened, refetchOnMountOrArgChange: true }
  );

  const completedOrders = useMemo(
    () => (historyData?.orders ?? []).filter(
      o => (o.status === KitchenOrderStatus.Completed || o.status === 'Completed' as any) && o.completedAt,
    ),
    [historyData],
  );

  // Fetch available areas
  const { data: areas } = useGetKitchenAreasQuery();

  // Fetch kitchen stats (for average time metric only — counts come from ordersResponse which is already filtered)
  // Igual que las órdenes: refetch por SignalR, polling de 60s como fallback.
  const { data: stats, refetch: refetchStats } = useGetKitchenStatsQuery(undefined, {
    pollingInterval: 60000,
    skip: !kdsEnabled,
  });

  // Refetch inmediato cuando el backend avisa que algo cambió en cocina, y también al
  // recuperar la conexión (para ponerse al día de eventos perdidos). Coalescemos ráfagas
  // (p.ej. comandear crea 2 comandas → 2 eventos) en un solo refetch.
  const realtimeRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleRealtimeRefetch = () => {
    if (realtimeRefetchTimerRef.current) return;
    realtimeRefetchTimerRef.current = setTimeout(() => {
      realtimeRefetchTimerRef.current = null;
      refetch();
      refetchStats();
    }, 300);
  };
  useHubEvent(KITCHEN_ORDERS_CHANGED_EVENT, scheduleRealtimeRefetch, kdsEnabled);
  useHubEvent(RECONNECTED_EVENT, scheduleRealtimeRefetch, kdsEnabled);
  useEffect(() => {
    return () => {
      if (realtimeRefetchTimerRef.current) clearTimeout(realtimeRefetchTimerRef.current);
    };
  }, []);

  // Toggle KDS enabled setting
  const handleToggleKdsEnabled = async (checked: boolean) => {
    try {
      await updateSetting({
        key: 'KitchenDisplayEnabled',
        value: String(checked),
      }).unwrap();
      message.success(checked ? 'Pantalla de cocina activada' : 'Pantalla de cocina desactivada');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al actualizar configuración');
    }
  };

  // Mutations
  const [markReady, { isLoading: isMarkingReady }] = useMarkKitchenOrderReadyMutation();
  const [markCompleted, { isLoading: isMarkingCompleted }] = useMarkKitchenOrderCompletedMutation();
  const [completeItems, { isLoading: isCompletingItems }] = useCompleteKitchenOrderItemsMutation();
  const [updateItemsStatus] = useUpdateKitchenOrderItemsStatusMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateKitchenOrderStatusMutation();

  // Kitchen Notes queries and mutations
  const { data: kitchenNotes = [], isLoading: isLoadingNotes } = useGetKitchenNotesQuery();
  const [createNote, { isLoading: isCreatingNote }] = useCreateKitchenNoteMutation();
  const [deleteNote, { isLoading: isDeletingNote }] = useDeleteKitchenNoteMutation();

  // Products query and mutation for availability
  const { data: productsResponse, isLoading: isLoadingProducts } = useGetProductsQuery({});
  const [updateProductAvailability, { isLoading: isUpdatingAvailability }] = useUpdateProductAvailabilityMutation();

  // Filter products by search term
  const filteredProducts = useMemo(() => {
    if (!productsResponse) return [];
    const activeProducts = productsResponse.filter(p => p.isActive);
    if (!productSearch.trim()) return activeProducts;
    const searchLower = productSearch.toLowerCase();
    return activeProducts.filter(p =>
      p.name.toLowerCase().includes(searchLower) ||
      p.categoryName?.toLowerCase().includes(searchLower)
    );
  }, [productsResponse, productSearch]);

  // Track which orders have been accepted (to stop alarm for them)
  const [acceptedOrders, setAcceptedOrders] = useState<Set<string>>(new Set());

  // Continuous alarm interval ref
  const alarmIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update elapsed time every second for visual updates
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Check if there are unaccepted pending orders
  const hasUnacceptedPendingOrders = useMemo(() => {
    if (!ordersResponse?.orders) return false;
    return ordersResponse.orders.some(order => {
      const effectiveStatus = order.filteredStatus ?? order.status;
      const statusStr = String(effectiveStatus);
      const isPending = statusStr === 'Pending' || statusStr === KitchenOrderStatus.Pending;
      return isPending && !acceptedOrders.has(order.id);
    });
  }, [ordersResponse?.orders, acceptedOrders]);

  // Continuous alarm effect - plays repeatedly while there are unaccepted pending orders
  useEffect(() => {
    // Clear any existing interval
    if (alarmIntervalRef.current) {
      clearInterval(alarmIntervalRef.current);
      alarmIntervalRef.current = null;
    }

    if (soundEnabled && hasUnacceptedPendingOrders) {
      // Play immediately
      playNotificationSound();

      // Then play every 3 seconds until accepted
      alarmIntervalRef.current = setInterval(() => {
        playNotificationSound();
      }, 3000);
    }

    return () => {
      if (alarmIntervalRef.current) {
        clearInterval(alarmIntervalRef.current);
        alarmIntervalRef.current = null;
      }
    };
  }, [soundEnabled, hasUnacceptedPendingOrders]);

  // Clean up accepted orders when they're no longer in the list
  useEffect(() => {
    if (ordersResponse?.orders) {
      const currentOrderIds = new Set(ordersResponse.orders.map(o => o.id));
      setAcceptedOrders(prev => {
        const newSet = new Set<string>();
        prev.forEach(id => {
          if (currentOrderIds.has(id)) {
            newSet.add(id);
          }
        });
        return newSet;
      });
    }
  }, [ordersResponse?.orders]);

  // Toggle sound notifications
  const toggleSound = () => {
    setSoundEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('kitchenSoundEnabled', String(newValue));
      if (newValue) {
        message.success('Alarma de nuevas órdenes activada');
      } else {
        message.info('Alarma de nuevas órdenes desactivada');
      }
      return newValue;
    });
  };

  // Handle create kitchen note
  const handleCreateNote = async () => {
    if (!newNoteMessage.trim()) {
      message.warning('Escribe un mensaje para la nota');
      return;
    }
    try {
      await createNote({ message: newNoteMessage.trim() }).unwrap();
      setNewNoteMessage('');
      message.success('Nota agregada');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al crear nota');
    }
  };

  // Handle delete kitchen note
  const handleDeleteNote = async (id: string) => {
    try {
      await deleteNote(id).unwrap();
      message.success('Nota eliminada');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al eliminar nota');
    }
  };

  // Handle toggle product availability
  const handleToggleProductAvailability = async (productId: string, isAvailable: boolean) => {
    try {
      await updateProductAvailability({ id: productId, isAvailable }).unwrap();
      message.success(isAvailable ? 'Producto disponible' : 'Producto no disponible');
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al actualizar disponibilidad');
    }
  };

  // Calculate current elapsed time for each order.
  // For daily-menu orders, use `phaseStartedAt` so the timer resets each time a course is
  // dispatched (backend advances this field on each dispatch).
  const ordersWithUpdatedTime = useMemo(() => {
    if (!ordersResponse?.orders) return [];

    return ordersResponse.orders.map(order => {
      const effectiveStart = order.phaseStartedAt ?? order.sentAt;
      const startMs = new Date(effectiveStart).getTime();
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - startMs) / 1000);
      return { ...order, elapsedSeconds };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersResponse?.orders, tick]);

  // Calculate grouped dishes (only for Pending/Preparing orders)
  const groupedDishes = useMemo(() => {
    return groupAllOrdersByDish(ordersWithUpdatedTime);
  }, [ordersWithUpdatedTime]);

  // Split orders into status lanes (Nuevas / En preparación / Listas), oldest first
  // within each lane so the most urgent card is always top-left.
  const lanes = useMemo(() => {
    const nuevas: KdsOrderWithTime[] = [];
    const preparando: KdsOrderWithTime[] = [];
    const listas: KdsOrderWithTime[] = [];
    const despachadas: KdsOrderWithTime[] = [];
    for (const order of ordersWithUpdatedTime) {
      switch (effectiveStatusOf(order)) {
        case 'Pending': nuevas.push(order); break;
        case 'Preparing': preparando.push(order); break;
        case 'Ready': listas.push(order); break;
        default: despachadas.push(order); break;
      }
    }
    const oldestFirst = (a: KdsOrderWithTime, b: KdsOrderWithTime) =>
      b.elapsedSeconds - a.elapsedSeconds;
    nuevas.sort(oldestFirst);
    preparando.sort(oldestFirst);
    listas.sort(oldestFirst);
    return [
      { key: 'new', className: 'kds-lane--new', label: 'Nuevas', orders: nuevas },
      { key: 'prep', className: 'kds-lane--prep', label: 'En preparación', orders: preparando },
      { key: 'ready', className: 'kds-lane--ready', label: 'Listas para entregar', orders: listas },
      { key: 'done', className: 'kds-lane--done', label: 'Despachadas', orders: despachadas },
    ];
  }, [ordersWithUpdatedTime]);

  // Accept order (stops alarm, changes to Preparing)
  const handleAcceptOrder = async (id: string) => {
    try {
      // Mark as accepted locally first (stops alarm immediately)
      setAcceptedOrders(prev => new Set(prev).add(id));

      // If production center filter is active, use item-level status update
      if (productionCenterFilters.length > 0) {
        const order = ordersResponse?.orders.find(o => o.id === id);
        if (order) {
          const itemIds = order.items
            .filter(item => !item.isCancelled && !item.isCompleted)
            .map(item => item.id);
          if (itemIds.length > 0) {
            await updateItemsStatus({ id, kitchenOrderItemIds: itemIds, status: KitchenOrderStatus.Preparing }).unwrap();
            message.success('Orden aceptada');
            refetch();
            return;
          }
        }
      }

      // No filter: order-level status update
      await updateStatus({
        id,
        data: { status: KitchenOrderStatus.Preparing }
      }).unwrap();

      message.success('Orden aceptada');
      refetch();
    } catch (error: any) {
      // Remove from accepted if failed
      setAcceptedOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      message.error(error?.data?.message || 'Error al aceptar orden');
    }
  };

  const handleMarkReady = async (id: string) => {
    try {
      // If production center filter is active, use item-level status update
      if (productionCenterFilters.length > 0) {
        const order = ordersResponse?.orders.find(o => o.id === id);
        if (order) {
          const itemIds = order.items
            .filter(item => !item.isCancelled && !item.isCompleted)
            .map(item => item.id);
          if (itemIds.length > 0) {
            await updateItemsStatus({ id, kitchenOrderItemIds: itemIds, status: KitchenOrderStatus.Ready }).unwrap();
            message.success('Orden marcada como lista');
            refetch();
            return;
          }
        }
      }

      // No filter: order-level
      await markReady(id).unwrap();
      message.success('Orden marcada como lista');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al marcar como lista');
    }
  };

  const handleMarkCompleted = async (id: string) => {
    try {
      // If production center filter is active, use partial dispatch (complete only visible items)
      if (productionCenterFilters.length > 0) {
        const order = ordersResponse?.orders.find(o => o.id === id);
        if (order) {
          const nonCompletedItemIds = order.items
            .filter(item => !item.isCompleted && !item.isCancelled)
            .map(item => item.id);
          if (nonCompletedItemIds.length > 0) {
            await completeItems({ id, kitchenOrderItemIds: nonCompletedItemIds }).unwrap();
            message.success('Items despachados');
            refetch();
            return;
          }
        }
      }
      // No filter or no items to partially dispatch: full dispatch
      await markCompleted(id).unwrap();
      message.success('Orden despachada');
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al despachar');
    }
  };

  // Daily-menu per-course dispatch: completes only the given course's items. The card
  // computes which items belong to the next course; this just fires the mutation.
  const handleDispatchCourse = async (orderId: string, itemIds: string[], courseName: string) => {
    if (itemIds.length === 0) return;
    try {
      const result = await completeItems({ id: orderId, kitchenOrderItemIds: itemIds }).unwrap();
      const completed = String(result.status) === 'Completed' || result.status === KitchenOrderStatus.Completed;
      message.success(completed ? 'Orden completada' : `${courseName} despachado`);
      refetch();
    } catch (error: any) {
      message.error(error?.data?.message || 'Error al despachar');
    }
  };

  const [isDispatchingAll, setIsDispatchingAll] = useState(false);

  const handleDispatchAllOrders = () => {
    const ordersToDispatch = ordersResponse?.orders || [];

    if (ordersToDispatch.length === 0) {
      message.info('No hay órdenes para despachar');
      return;
    }

    modal.confirm({
      title: '¿Despachar todas las órdenes?',
      icon: <ExclamationCircleOutlined />,
      content: `Se despacharán ${ordersToDispatch.length} orden(es) de la pantalla. Esta acción no se puede deshacer.`,
      okText: 'Sí, despachar todas',
      cancelText: 'Cancelar',
      okType: 'danger',
      onOk: async () => {
        setIsDispatchingAll(true);
        let successCount = 0;
        let errorCount = 0;

        for (const order of ordersToDispatch) {
          try {
            // If production center filter is active, use partial dispatch per order
            if (productionCenterFilters.length > 0) {
              const nonCompletedItemIds = order.items
                .filter(item => !item.isCompleted && !item.isCancelled)
                .map(item => item.id);
              if (nonCompletedItemIds.length > 0) {
                await completeItems({ id: order.id, kitchenOrderItemIds: nonCompletedItemIds }).unwrap();
              }
            } else {
              await markCompleted(order.id).unwrap();
            }
            successCount++;
          } catch (error) {
            errorCount++;
            console.error(`Error despachando orden ${order.id}:`, error);
          }
        }

        setIsDispatchingAll(false);
        refetch();

        if (errorCount === 0) {
          message.success(`${successCount} orden(es) despachada(s) exitosamente`);
        } else {
          message.warning(`${successCount} despachada(s), ${errorCount} con error`);
        }
      },
    });
  };


  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Cargando pedidos...</Text>
        </div>
      </div>
    );
  }

  return (
    <ConfigProvider theme={{ algorithm: antdTheme.darkAlgorithm, token: { colorPrimary: '#30b7b2' } }}>
    <div ref={pageRef} className={`kds-page${isMobile ? ' kds-page--mobile' : ''}${isFullscreen ? ' kds-page--max' : ''}`}>
      {/* Single top bar: title + live counters + controls. Stats live here as chips so
          the orders start as high as possible — screen real estate is for comandas. */}
      <div className="kds-bar">
        <div className="kds-bar__title">
          <span className="kds-bar__name">Cocina</span>
        </div>

        {kdsEnabled && ordersResponse && (
          <div className="kds-chips">
            <span className="kds-chip">
              <span className="kds-chip__dot kds-chip__dot--warn" />
              <b>{ordersResponse.pendingCount}</b> nuevas
            </span>
            <span className="kds-chip">
              <span className="kds-chip__dot kds-chip__dot--blue" />
              <b>{ordersResponse.preparingCount}</b> en prep.
            </span>
            <span className="kds-chip">
              <span className="kds-chip__dot kds-chip__dot--ok" />
              <b>{ordersResponse.readyCount}</b> listas
            </span>
            <span className="kds-chip">
              prom <b>{(stats?.averageTimeToReadyMinutes ?? 0).toFixed(1)}</b> min
            </span>
          </div>
        )}

        <div className="kds-bar__spacer" />

        <Space wrap>
          {/* KDS Enable Switch - only show when no production centers exist */}
          {!hasProductionCenters && (
            <Tooltip title="Habilita o deshabilita la recepción de comandas en esta pantalla">
              <Space>
                <Text type="secondary">KDS</Text>
                <Switch
                  checked={kdsEnabledSetting?.value ?? false}
                  onChange={handleToggleKdsEnabled}
                  loading={isUpdatingSetting || isLoadingKdsSetting}
                />
              </Space>
            </Tooltip>
          )}

          {/* Production Center Filter - only show when centers exist (multi-select) */}
          {hasProductionCenters && productionCenters.length > 0 && (
            <Select
              mode="multiple"
              size="small"
              placeholder="Centros de producción"
              allowClear
              style={{ minWidth: 200, maxWidth: isMobile ? 200 : 350 }}
              value={productionCenterFilters}
              onChange={setProductionCenterFilters}
              maxTagCount={isMobile ? 1 : 2}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} más`}
            >
              {productionCenters.map(pc => (
                <Option key={pc.id} value={pc.id}>{pc.name}</Option>
              ))}
            </Select>
          )}

          <Select
            size="small"
            placeholder="Filtrar por área"
            allowClear
            style={{ width: 150 }}
            value={areaFilter}
            onChange={setAreaFilter}
          >
            {areas?.map(area => (
              <Option key={area} value={area}>{area}</Option>
            ))}
          </Select>

          <Tooltip title={soundEnabled ? 'Desactivar alarma' : 'Activar alarma'}>
            <Button
              size="small"
              icon={<SoundOutlined />}
              onClick={toggleSound}
              type={soundEnabled ? 'primary' : 'default'}
              style={soundEnabled ? { background: '#52c41a', borderColor: '#52c41a' } : {}}
            >
              {!isMobile && (soundEnabled ? 'Alarma ON' : 'Alarma OFF')}
            </Button>
          </Tooltip>

          <Segmented
            size="small"
            value={kdsView}
            onChange={(val) => setKdsView(val as KdsViewMode)}
            options={[
              { value: 'lanes', icon: <UnorderedListOutlined />, label: isMobile ? undefined : 'Carriles', title: 'Carriles por estado' },
              { value: 'dish', icon: <AppstoreOutlined />, label: isMobile ? undefined : 'Por platillo', title: 'Agrupar por platillo' },
              { value: 'rails', icon: <TagsOutlined />, label: isMobile ? undefined : 'Rieles', title: 'Comandas colgando del riel' },
            ]}
          />

          <Tooltip title="Gestionar notas y disponibilidad de productos">
            <Button
              size="small"
              icon={<SettingOutlined />}
              onClick={() => setDrawerVisible(true)}
              type={kitchenNotes.length > 0 ? 'primary' : 'default'}
              style={kitchenNotes.length > 0 ? { background: 'var(--kds-warn)', borderColor: 'var(--kds-warn)', color: '#181203' } : {}}
            >
              {!isMobile && 'Gestión'}
              {kitchenNotes.length > 0 && (
                <Badge count={kitchenNotes.length} size="small" style={{ marginLeft: 4 }} />
              )}
            </Button>
          </Tooltip>

          <Tooltip title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
            <Button
              size="small"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Orders */}
      {!kdsEnabled ? (
        <Empty
          description={
            <Space direction="vertical" align="center">
              <Text>La pantalla de cocina está desactivada</Text>
              <Text type="secondary">
                Active el switch "KDS" arriba para comenzar a recibir comandas
              </Text>
            </Space>
          }
          style={{ marginTop: 50 }}
        />
      ) : kdsView === 'dish' ? (
        // Grouped by Dish View
        groupedDishes.length === 0 ? (
          <Empty
            description="No hay platillos pendientes"
            style={{ marginTop: 50 }}
          />
        ) : (
          <div className="kds-lane__cards" style={{ marginTop: 16 }}>
            {groupedDishes.map(dish => (
              <GroupedDishCard key={dish.dishKey} dish={dish} />
            ))}
          </div>
        )
      ) : (
        // Order View: status lanes, oldest order first within each lane
        ordersWithUpdatedTime.length === 0 ? (
          <Empty
            description="No hay pedidos pendientes"
            style={{ marginTop: 50 }}
          />
        ) : kdsView === 'rails' ? (
          <KitchenRailsView
            lanes={lanes}
            acceptedOrders={acceptedOrders}
            soundEnabled={soundEnabled}
            onAccept={handleAcceptOrder}
            onMarkReady={handleMarkReady}
            onMarkCompleted={handleMarkCompleted}
            onDispatchCourse={handleDispatchCourse}
            accepting={isUpdatingStatus}
            markingReady={isMarkingReady}
            completing={isMarkingCompleted || isCompletingItems}
          />
        ) : (
          lanes.filter(lane => lane.orders.length > 0).map(lane => (
            <div key={lane.key} className={`kds-lane ${lane.className}`}>
              <div className="kds-lane__head">
                <span className="kds-lane__label">{lane.label}</span>
                <span className="kds-lane__count">{lane.orders.length}</span>
                <span className="kds-lane__rule" />
              </div>
              <div className="kds-lane__cards">
                {lane.orders.map(order => (
                  <KitchenOrderCard
                    key={order.id}
                    order={order}
                    isMobile={isMobile}
                    soundEnabled={soundEnabled}
                    isAccepted={acceptedOrders.has(order.id)}
                    onAccept={handleAcceptOrder}
                    onMarkReady={handleMarkReady}
                    onMarkCompleted={handleMarkCompleted}
                    onDispatchCourse={handleDispatchCourse}
                    accepting={isUpdatingStatus}
                    markingReady={isMarkingReady}
                    completing={isMarkingCompleted || isCompletingItems}
                  />
                ))}
              </div>
            </div>
          ))
        )
      )}

      {/* Management Drawer - Notes and Product Availability */}
      <Drawer
        title="Gestión de Cocina"
        placement="right"
        width={isMobile ? '100%' : 450}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        // The drawer portals to document.body, outside .kds-page — this class carries
        // the KDS dark-theme styles (kitchen.css) into the portal subtree.
        rootClassName="kds-drawer"
        extra={
          <Tooltip title="Despachar todas las órdenes de la pantalla">
            <Button
              icon={<ClearOutlined />}
              onClick={handleDispatchAllOrders}
              disabled={isDispatchingAll || !kdsEnabled}
              loading={isDispatchingAll}
              danger
            >
              Limpiar Todo
            </Button>
          </Tooltip>
        }
      >
        <Tabs
          defaultActiveKey="notes"
          onChange={(k) => { if (k === 'history') setHistoryTabOpened(true); }}
        >
          <TabPane tab="Notas para Meseros" key="notes">
            <div style={{ marginBottom: 16 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="Ej: Se acabó el bistec"
                  value={newNoteMessage}
                  onChange={(e) => setNewNoteMessage(e.target.value)}
                  onPressEnter={handleCreateNote}
                  maxLength={500}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateNote}
                  loading={isCreatingNote}
                >
                  Agregar
                </Button>
              </Space.Compact>
            </div>

            {isLoadingNotes ? (
              <Spin />
            ) : kitchenNotes.length === 0 ? (
              <Empty
                description="No hay notas activas"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={kitchenNotes}
                renderItem={(note) => (
                  <List.Item
                    className="kds-note-item"
                    actions={[
                      <Button
                        key="delete"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteNote(note.id)}
                        loading={isDeletingNote}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<WarningOutlined style={{ color: 'var(--color-warning)', fontSize: 20 }} />}
                      title={note.message}
                      description={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {note.createdByUserName} - {formatInTimeZone(note.createdAt, 'DD/MM/YYYY HH:mm:ss')}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </TabPane>

          <TabPane tab="Disponibilidad" key="availability">
            <Input
              placeholder="Buscar producto..."
              prefix={<SearchOutlined />}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              style={{ marginBottom: 16 }}
              allowClear
            />

            {isLoadingProducts ? (
              <Spin />
            ) : filteredProducts.length === 0 ? (
              <Empty
                description={productSearch ? 'No se encontraron productos' : 'No hay productos'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List<Product>
                dataSource={filteredProducts}
                renderItem={(product) => (
                  <List.Item
                    className={`kds-product-item${product.isAvailable ? '' : ' kds-product-item--out'}`}
                    actions={[
                      <Switch
                        key="switch"
                        checked={product.isAvailable}
                        onChange={(checked) => handleToggleProductAvailability(product.id, checked)}
                        loading={isUpdatingAvailability}
                        checkedChildren="Disp."
                        unCheckedChildren="No disp."
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Text
                          style={{
                            textDecoration: product.isAvailable ? 'none' : 'line-through',
                            color: product.isAvailable ? undefined : '#ff4d4f',
                          }}
                        >
                          {product.name}
                        </Text>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {product.categoryName}
                          {!product.isAvailable && (
                            <Tag color="error" style={{ marginLeft: 8 }}>No disponible</Tag>
                          )}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
                style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}
              />
            )}
          </TabPane>

          {isInventarioEnabled && (
            <TabPane tab="Stock" key="stock">
              <KitchenStockDrawerTab />
            </TabPane>
          )}

          <TabPane tab="Historial" key="history">
            {historyLoading ? (
              <Spin />
            ) : completedOrders.length === 0 ? (
              <Empty
                description="Sin comandas despachadas en este turno"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                <Collapse accordion ghost>
                  {completedOrders.map(order => (
                    <Panel
                      key={order.id}
                      header={
                        <Space wrap size={4}>
                          <Text strong>#{order.orderNumber}</Text>
                          {(order.tableName || order.tableNumber) && (
                            <Text type="secondary">
                              Mesa {order.tableName || order.tableNumber}
                            </Text>
                          )}
                          {order.areaName && (
                            <Text type="secondary">· {order.areaName}</Text>
                          )}
                          <Text type="secondary">· {order.waiterName}</Text>
                          <Tag>
                            {formatInTimeZone(order.completedAt!, 'HH:mm')}
                          </Tag>
                          <Tag color="blue">
                            {prepDurationMinutes(order.sentAt, order.completedAt!)} min
                          </Tag>
                        </Space>
                      }
                    >
                      {renderHistoryBody(order)}
                    </Panel>
                  ))}
                </Collapse>
              </div>
            )}
          </TabPane>
        </Tabs>
      </Drawer>

    </div>
    </ConfigProvider>
  );
};

export default KitchenDisplay;
